/**
 * features/recurring/recurringWrite.ts
 * Recurring invoice templates. "Generate" materialises a real issued
 * invoice from the template and advances the next run date.
 */
import { collection, deleteDoc, doc } from 'firebase/firestore';
import { db, addDoc, updateDoc } from '@/lib/firebase';
import { buildLineItems, computeTotals } from '@/lib/money';
import { clientSnapshot, issueInvoice } from '@/features/invoices/invoicesWrite';
import {
  COLLECTIONS,
  type Client,
  type RecurrenceFrequency,
  type RecurringInvoice,
  type VatSnapshot,
  type VatTreatment,
} from '@/types';

export interface TemplateLine {
  description: string;
  quantity: number;
  unitPrice: number;
  vatTreatment: VatTreatment;
}

export interface RecurringPayload {
  clientId: string;
  lineItems: TemplateLine[];
  frequency: RecurrenceFrequency;
  startDate: string;
  paymentTermDays: number;
  notes?: string;
}

const MONTHS: Record<RecurrenceFrequency, number> = { monthly: 1, quarterly: 3, annual: 12 };

export function advanceDate(iso: string, frequency: RecurrenceFrequency): string {
  const d = new Date(iso);
  d.setMonth(d.getMonth() + MONTHS[frequency]);
  return d.toISOString().slice(0, 10);
}

function addDays(iso: string, days: number): string {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function baseDoc(p: RecurringPayload) {
  return {
    clientId: p.clientId,
    lineItems: p.lineItems,
    frequency: p.frequency,
    startDate: p.startDate,
    paymentTermDays: p.paymentTermDays,
    notes: p.notes?.trim() || undefined,
    updatedAt: new Date().toISOString(),
  };
}

export async function createTemplate(p: RecurringPayload, actor: string): Promise<string> {
  const ref = await addDoc(collection(db, COLLECTIONS.recurringInvoices), {
    ...baseDoc(p),
    active: true,
    nextRunDate: p.startDate,
    createdAt: new Date().toISOString(),
    createdBy: actor,
  });
  return ref.id;
}

export async function updateTemplate(id: string, p: RecurringPayload): Promise<void> {
  await updateDoc(doc(db, COLLECTIONS.recurringInvoices, id), baseDoc(p));
}

export async function setTemplateActive(id: string, active: boolean): Promise<void> {
  await updateDoc(doc(db, COLLECTIONS.recurringInvoices, id), { active, updatedAt: new Date().toISOString() });
}

export async function deleteTemplate(id: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTIONS.recurringInvoices, id));
}

/** Materialise an issued invoice from the template and advance the schedule. */
export async function generateNow(
  template: RecurringInvoice,
  client: Client,
  vat: VatSnapshot,
  actor: string,
): Promise<{ invoiceId: string; number: string }> {
  const issueDate = template.nextRunDate;
  const dueDate = addDays(issueDate, template.paymentTermDays);
  const built = buildLineItems(template.lineItems, vat);
  const totals = computeTotals(built);

  const result = await issueInvoice(
    {
      clientId: template.clientId,
      client: clientSnapshot(client),
      issueDate,
      dueDate,
      lineItems: built,
      totals,
      vat,
      notes: template.notes,
    },
    actor,
  );

  const now = new Date().toISOString();
  await updateDoc(doc(db, COLLECTIONS.recurringInvoices, template.id), {
    nextRunDate: advanceDate(template.nextRunDate, template.frequency),
    lastGeneratedInvoiceId: result.id,
    lastGeneratedAt: now,
    updatedAt: now,
  });

  return { invoiceId: result.id, number: result.number };
}
